import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { OrganizationsService } from '../../services/organizations.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Filter, Pagination, Sort, SortDir } from '../../../../api/services/abstract-api.service';
import { isEmpty, isEqual } from 'lodash-es';
import { OrganizationData } from '../../interfaces/OrganizationData';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { VirtualScrollApiDataSource } from '../../../../api/data-sources/virtual-scroll.api-data-source';
import { distinctUntilChanged } from 'rxjs/operators';
import { SecurityaddonsService } from '../../../securityaddons/services/securityaddons.service';
import { MatSelect } from '@angular/material/select';
import { CompaniesService } from '../../../companies/services/companies.service';
import { IdentityService } from '../../../auth/services/identity.service';
import { CompanyDetailsInterface } from '../../../companies/interfaces/company-details.interface';
import { CompanyDataModel } from '../../../companies/company-data-model.model';

@Component({
    selector: 'app-organization-selector',
    templateUrl: './organization-selector.component.html',
    styleUrls: ['./organization-selector.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class OrganizationSelectorComponent implements OnInit, OnDestroy {
    /**  selectorPlaceholder - default value for selector */
    @Input() selectorPlaceholder = $localize`Company: All managed companies`;

    /**  showGlobalOption - show selector or not inside working page */
    @Input() showGlobalOption = true;

    /**  isGlobalSelector - the selected organization from the selector will affect all pages */
    @Input() isGlobalSelector = true;

    /**  disableSelector - enable/disable selector */
    @Input() disableSelector = false;

    /**  resetSelector - reset current selection */
    @Input() resetSelector = false;
    @Output() organizationChange: EventEmitter<OrganizationData | null> = new EventEmitter<OrganizationData | null>();

    @ViewChild(CdkVirtualScrollViewport)
    organizationsVirtualViewport: CdkVirtualScrollViewport;

    @ViewChild('organizationSelector') organizationSelector: MatSelect;
    @ViewChild('selectorSearch') selectorSearch: ElementRef;
    public organizationsDataSource: VirtualScrollApiDataSource<OrganizationData>;
    public hasAppliedFilter = false;
    public CompaniesPerPage = 100;
    public DisplayedCompanies = 9;
    public LoadingMessage = $localize`Loading...`;

    private paginationSubject = new BehaviorSubject<Pagination>({
        page: 1,
        limit: this.CompaniesPerPage,
    });
    private sortSubject = new BehaviorSubject<Sort>({
        field: 'name',
        type: SortDir.Asc,
    });
    private filterSubject = new BehaviorSubject<Filter>({});

    private selectedOrganization: OrganizationData | null;
    private selectedOrganizationSub: Subscription;

    constructor(
        private organizationsService: OrganizationsService,
        public companiesService: CompaniesService,
        public identityService: IdentityService,
        private securityAddOnService: SecurityaddonsService,
        private cd: ChangeDetectorRef,
    ) {
        if (this.identityService.isCompanySelectorVisible()) {
            this.organizationsService.initializeSelectedOrganization(this.SelectedOrganization);
        }
    }

    public get SelectedOrganization(): OrganizationData | null {
        return this.selectedOrganization;
    }

    public set SelectedOrganization(organization: OrganizationData | null) {
        this.selectedOrganization = organization;

        if (organization) {
            this.organizationsService.selectOrganization(organization, this.isGlobalSelector);
        } else {
            this.organizationsService.selectAllOrganizations();
        }
    }

    public get SearchFilter(): string {
        return this.filterSubject.value.name;
    }

    public set SearchFilter(nameFilter: string) {
        this.hasAppliedFilter = !isEmpty(nameFilter);
        this.filterSubject.next({
            name: nameFilter,
        });
    }

    public ngOnInit(): void {
        this.organizationsDataSource = new VirtualScrollApiDataSource<OrganizationData>(this.organizationsService);
        this.organizationsDataSource.PaginationObservable = this.paginationSubject;
        this.organizationsDataSource.SortObservable = this.sortSubject;
        this.organizationsDataSource.FilterObservable = this.filterSubject;

        /* reset selector company in Create and Edit Companies/Users/Reports/Documents */
        if (this.resetSelector) {
            this.organizationsService.selectOrganization(null, this.isGlobalSelector);
        }

        /* if a component other than this one changed the organization, update it, and emit the change */
        this.selectedOrganizationSub = this.organizationsService
            .selectedOrganization$(this.isGlobalSelector)
            .pipe(distinctUntilChanged(isEqual))
            .subscribe({
                next: (organization: OrganizationData | null) => {
                    this.selectedOrganization = organization;
                    this.organizationChange.emit(organization);
                },
            });
    }

    /**
     * Listen to selector opening
     * @param $event
     */
    public onSelectorOpenedChange($event: boolean) {
        /* check if selector has been opened (not closed) to reset viewport's properties */
        if (!$event) {
            return;
        }
        // this.selectorSearch.focus();
        // this.cd.detectChanges();
        // this.organizationsVirtualViewport.scrollToIndex(0);
        this.organizationsVirtualViewport.checkViewportSize();
    }

    /**
     * Handler for scrolling through selector options
     * @param firstDisplayedItemIndex
     */
    public onScroll(firstDisplayedItemIndex: number) {
        /* take into account the 'All managed companies' option */
        if (this.hasAppliedFilter || !this.showGlobalOption) {
            this.organizationsDataSource.onScroll(
                firstDisplayedItemIndex,
                Math.min(firstDisplayedItemIndex + this.DisplayedCompanies - 1, this.organizationsDataSource.Data.length),
            );
        } else {
            this.organizationsDataSource.onScroll(
                Math.max(0, firstDisplayedItemIndex - 1),
                Math.min(firstDisplayedItemIndex + this.DisplayedCompanies - 2, this.organizationsDataSource.Data.length),
            );
        }
    }

    ngOnDestroy(): void {
        this.selectedOrganizationSub?.unsubscribe();
    }

    public onKeyDown(event: KeyboardEvent) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
        if (event.key === 'Enter') {
            // this.selectorSearch.focus();
            this.organizationSelector.open();
            setTimeout(() => {
                this.selectorSearch.nativeElement.focus();
            }, 0);
        }

        if (event.code === 'Tab' && this.SearchFilter && this.SearchFilter !== '') {
            this.organizationChange.emit(this.organizationsDataSource.Data[0]);
            this.SelectedOrganization = this.organizationsDataSource.Data[0];
        }

        if (event.code === 'Space') {
            event.stopPropagation();
        }
    }
}
